/**
 * 'Show more' for the PDP 'Who it is for' block (snippets/pdp-cb-audience.liquid).
 *
 * Each group's values are cut to the block's 'Rows shown' setting — one for
 * desktop, one for mobile, split at 768px like product.css — and a '+N more'
 * link ends the last visible row. Tapping it shows every value for good; the
 * link goes and focus moves to the first value it revealed. A group that
 * already fits, or would only hide one value, is left alone: '+1 more' costs
 * as much room as the value it hides.
 *
 * Rows are counted from where the chips actually wrap, so the cut follows the
 * column width. If the link itself would wrap onto a new row, the last visible
 * value is hidden too, until it fits.
 *
 * The block's row is usually closed on load, and nothing can be measured while
 * it is. Current browsers still give a closed <details>' content a size, so the
 * ResizeObserver can't be trusted to wait: a list that isn't rendered is
 * skipped, and the row's 'toggle' lays every list out again once it opens. The
 * observer then only handles width changes. Until a list has been measured,
 * pdp-collapsible.css clips it to the same number of rows, so values never
 * flash in and then disappear.
 *
 * The clip waits for this element to be defined, so if this file never loads
 * every value shows instead.
 */
if (!customElements.get('pdp-audience')) {
  const MOBILE = window.matchMedia('(max-width: 768px)');
  let uid = 0;

  class PdpAudience extends HTMLElement {
    connectedCallback() {
      this.lists = [...this.querySelectorAll('.pdp-chips')];
      this.widths = new WeakMap();
      this.expanded = new WeakSet();

      // Width only: hiding values changes the list's height, which would
      // otherwise re-run the layout that caused it.
      this.observer = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const width = Math.round(entry.contentRect.width);
          if (width === 0 || this.widths.get(entry.target) === width) return;
          this.widths.set(entry.target, width);
          this.layout(entry.target);
        });
      });
      this.lists.forEach((list) => this.observer.observe(list));

      this.handleClick = this.handleClick.bind(this);
      this.layoutAll = () => this.lists.forEach((list) => this.layout(list));
      this.handleToggle = () => {
        if (this.details.open) this.layoutAll();
      };
      this.addEventListener('click', this.handleClick);
      MOBILE.addEventListener('change', this.layoutAll);

      this.details = this.closest('details');
      if (this.details) this.details.addEventListener('toggle', this.handleToggle);
    }

    disconnectedCallback() {
      this.observer.disconnect();
      this.removeEventListener('click', this.handleClick);
      MOBILE.removeEventListener('change', this.layoutAll);
      if (this.details) this.details.removeEventListener('toggle', this.handleToggle);
    }

    get rows() {
      const value = MOBILE.matches ? this.dataset.rowsMobile : this.dataset.rowsDesktop;
      return parseInt(value, 10) || 0;
    }

    handleClick(evt) {
      const button = evt.target.closest('.pdp-chip--more button');
      if (!button || !this.contains(button)) return;

      const list = button.closest('.pdp-chips');
      const firstHidden = list.querySelector(':scope > .is-overflow:not(.pdp-chip--more)');
      this.expanded.add(list);
      this.layout(list);

      // The link is gone, so focus would drop to the page. Hand it to the first
      // value it revealed: its link if it has one, else the item itself.
      if (!firstHidden) return;
      let target = firstHidden.querySelector('a');
      if (!target) {
        firstHidden.tabIndex = -1;
        target = firstHidden;
      }
      target.focus({ preventScroll: true });
    }

    /**
     * Lays one list out from scratch: every value shown, rows counted, then the
     * overflow hidden behind the '+N more' link.
     * @param {HTMLElement} list - A .pdp-chips list.
     */
    layout(list) {
      // Closed row: measuring now would see every value on one line. The CSS
      // clip stays on until the row opens and 'toggle' comes back here.
      const rendered =
        typeof list.checkVisibility === 'function' ? list.checkVisibility() : list.getClientRects().length > 0;
      if (!rendered) return;

      const chips = [...list.children].filter((el) => !el.classList.contains('pdp-chip--more'));
      let more = list.querySelector(':scope > .pdp-chip--more');

      chips.forEach((chip) => chip.classList.remove('is-overflow'));
      if (more) more.classList.add('is-overflow');

      // Lifts the CSS clip, so every row can be measured.
      list.dataset.rowsReady = '';

      const rows = this.rows;
      if (!rows || this.expanded.has(list)) return;

      const tops = [...new Set(chips.map((chip) => chip.offsetTop))];
      if (tops.length <= rows) return;

      let visible = chips.filter((chip) => chip.offsetTop < tops[rows]).length;
      if (chips.length - visible < 2) return;

      if (!more) more = this.createMore(list);
      more.classList.remove('is-overflow');
      const button = more.firstElementChild;
      const lastTop = tops[rows - 1];

      for (;;) {
        chips.forEach((chip, index) => chip.classList.toggle('is-overflow', index >= visible));
        button.textContent = (this.dataset.moreLabel || '+[count] more').replace(
          '[count]',
          chips.length - visible
        );
        if (more.offsetTop <= lastTop || visible <= 1) break;
        visible -= 1;
      }
    }

    /**
     * @param {HTMLElement} list - The list the link expands.
     * @returns {HTMLElement} The new '+N more' list item.
     */
    createMore(list) {
      if (!list.id) {
        uid += 1;
        list.id = `pdp-chips-${uid}`;
      }

      const item = document.createElement('li');
      item.className = 'pdp-chip pdp-chip--more';

      const button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('aria-controls', list.id);
      button.setAttribute('aria-expanded', 'false');

      item.append(button);
      list.append(item);
      return item;
    }
  }

  customElements.define('pdp-audience', PdpAudience);
}
